import { getErrorMessage, instanceOfWebRPCError } from '$lib/utils/getErrorMessage';
import type { Readable, Writable } from 'svelte/store';
import { derived, writable } from 'svelte/store';
import * as Sentry from '@sentry/sveltekit';

const TIMEOUT = 2000; //fallback timeout
let id = 0;

export type ToastMessageType = {
	message: string;
	type: string;
	timeout: number;
	id: number;
};

type ToastMessageArrayType = Array<ToastMessageType>;

function createToastMessageStore() {
	const _notifications: Writable<ToastMessageArrayType> = writable([] as ToastMessageArrayType);

	const add = (message: string, type = 'default', timeout = 0) => {
		_notifications.update((state) => {
			return [...state, { id: id++, type, message, timeout }];
		});
	};
	const remove = (id: number) => {
		_notifications.update((state) => {
			const currentPosition = state.findIndex((element) => element.id === id);
			return [...state.slice(0, currentPosition), ...state.slice(currentPosition + 1)];
		});
	};

	const notifications: Readable<ToastMessageArrayType> = derived(
		_notifications,
		($_notifications, set) => {
			set($_notifications);
			if (
				$_notifications.length > 0 &&
				($_notifications[0].type !== 'danger' || $_notifications[0].timeout)
			) {
				const timer = setTimeout(() => {
					_notifications.update((state) => {
						state.shift();
						return state;
					});
				}, $_notifications[0].timeout);
				return () => {
					clearTimeout(timer);
				};
			}
		}
	);
	const { subscribe } = notifications;

	return {
		subscribe,
		remove,
		default: (msg: string, timeout?: number) => add(msg, 'default', timeout || TIMEOUT),
		danger: (msg: string, e?: unknown, timeout?: number) => {
			if (e !== undefined) {
				if (instanceOfWebRPCError(e)) {
					const error = new Error(e.msg, { ...e });
					Sentry.captureException(error);
				} else {
					Sentry.captureException(e);
				}
				add(`${msg}: ${getErrorMessage(e)}`, 'danger', timeout);
			} else {
				add(msg, 'danger', timeout);
			}
		},
		warning: (msg: string, timeout?: number) => add(msg, 'warning', timeout || TIMEOUT),
		info: (msg: string, timeout?: number) => add(msg, 'info', timeout || TIMEOUT),
		success: (msg: string, timeout?: number) => add(msg, 'success', timeout || TIMEOUT),
	};
}

export const toastMessages = createToastMessageStore();
